import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, combineLatest, takeUntil } from 'rxjs';
import { ChatAttachment, ChatConversation, ChatMessage } from '../../models/chat.models';
import { ChatInputPayload } from '../chat-input/chat-input.component';
import { ChatTurnController } from '../../services/chat-turn.controller';
import { ChatExportService } from '../../services/chat-export.service';
import { ChatService } from '../../services/chat.service';
import { UserService } from 'src/app/services/user.service';
import { AgentService } from '../../services/agent.service';
import { AgentDefinition } from '../../models/agent.models';

const DISCLAIMER_KEY = 'cafi_ai_disclaimer_v1';

@Component({
  selector: 'app-chat-page',
  templateUrl: './chat-page.component.html',
  styleUrls: ['./chat-page.component.scss'],
  providers: [ChatTurnController],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  activeId: string | null = null;
  messages: ReadonlyArray<ChatMessage> = [];
  conversations: ReadonlyArray<ChatConversation> = [];
  userName = '';
  agents: AgentDefinition[] = [];

  /** True when the Agents tab is active (info-only view). */
  showAgentDirectory = false;

  /** Emits the agent code currently streaming, null when idle. */
  activeAgentCode$!: Observable<string | null>;

  /** Mobile drawer state */
  sidebarOpen = false;

  /** AI Act Art. 50 disclaimer */
  showDisclaimerModal = false;

  constructor(
    private readonly chatService: ChatService,
    private readonly turnController: ChatTurnController,
    private readonly exportService: ChatExportService,
    private readonly userService: UserService,
    private readonly agentService: AgentService,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  get busy(): boolean { return this.turnController.busy; }

  ngOnInit(): void {
    document.body.classList.add('chat-page-active');
    this.showDisclaimerModal = !localStorage.getItem(DISCLAIMER_KEY);
    this.activeAgentCode$ = this.turnController.activeAgentCode$;

    combineLatest([this.chatService.conversations$, this.chatService.activeConversationId$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([list, activeId]) => {
        this.conversations = list;
        this.activeId = activeId;
        if (activeId) {
          const convo = this.chatService.getConversation(activeId);
          this.messages = convo?.messages ?? [];
        } else {
          this.messages = [];
        }
        this.cdr.markForCheck();
      });

    this.agents = this.agentService.agents;

    this.userService.getUserInfo().pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.userName = user?.name?.split(' ')[0] ?? '';
      this.cdr.markForCheck();
    });

    if (this.conversations.length === 0) {
      this.onNewChat();
    } else {
      this.chatService.setActive(this.conversations[0].id);
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('chat-page-active');
    this.turnController.cancel();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPick(id: string): void {
    this.chatService.setActive(id);
  }

  onRename(event: { id: string; title: string }): void {
    this.chatService.renameConversation(event.id, event.title);
  }

  onNewChat(): void {
    const title = this.translate.instant('chatAi.sidebar.untitled') as string;
    this.chatService.createConversation(title || 'New chat');
  }

  onSend(payload: ChatInputPayload | string): void {
    if (!this.activeId) return;
    if (typeof payload === 'string') {
      this.turnController.sendTurn(this.activeId, payload);
    } else {
      this.turnController.sendTurn(this.activeId, payload.text, payload.attachments);
    }
  }

  onSuggestionPick(prompt: string): void {
    this.onSend(prompt);
  }

  onSuggestionPickI18n(key: string): void {
    this.onSend(this.translate.instant(key) as string);
  }

  onNavigate(route: string): void {
    this.router.navigate([route]);
  }

  onCancel(): void {
    this.turnController.cancel();
  }

  /** Toggle agent directory view (called from sidebar tab) */
  onShowAgentDirectory(): void {
    this.showAgentDirectory = true;
  }

  /** Back to chat */
  onShowChat(): void {
    this.showAgentDirectory = false;
  }

  /** Returns icon + display label for the given agentCode, used in templates. */
  getAgentIcon(code: string): string {
    return this.agentService.getAgent(code)?.icon ?? '🤖';
  }
  getAgentColor(code: string): string {
    return this.agentService.getAgent(code)?.accentColor ?? '#00C48C';
  }

  // ── Mobile sidebar drawer ──────────────────────────────────────
  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
    this.cdr.markForCheck();
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
    this.cdr.markForCheck();
  }

  onDeleteChat(id: string): void {
    this.chatService.deleteConversation(id);
  }

  onDisclaimerAccept(): void {
    const record = { accepted: true, acceptedAt: new Date().toISOString(), userId: this.userName || 'unknown' };
    localStorage.setItem(DISCLAIMER_KEY, JSON.stringify(record));
    this.showDisclaimerModal = false;
    this.cdr.markForCheck();
  }

  onRetry(payload: { text: string; attachments?: ChatAttachment[] }): void {
    if (!this.activeId || !payload?.text) return;
    this.turnController.sendTurn(this.activeId, payload.text, payload.attachments);
  }

  onExportPdf(): void {
    if (!this.activeId || this.messages.length === 0) return;
    const convo = this.chatService.getConversation(this.activeId);
    const title = convo?.title ?? 'Conversación';
    this.exportService.exportToPdf(this.messages, title);
  }
}
